import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  type Fleet,
  type GetReservationsByFleetParams,
  getFleetByCity,
  getReservationConfigByCityId,
  getReservationsByFleet,
  getVehiclesList,
  type ReservationConfigWithDeposit,
  type ReservationWithResolution,
  type VehicleListItem,
} from "@/apis/reservations/apis";
import { queryKeys } from "./keys";

interface UseReservationsByFleetOptions {
  enabled?: boolean;
}

export function useReservationsByFleet(
  params: GetReservationsByFleetParams,
  options?: UseReservationsByFleetOptions
) {
  return useQuery<ReservationWithResolution[]>({
    queryKey: queryKeys.reservations.list(params),
    queryFn: () => getReservationsByFleet(params),
    enabled: options?.enabled !== false && Boolean(params.fleet_id),
    placeholderData: keepPreviousData,
  });
}

export function useReservationsVehiclesList() {
  return useQuery<VehicleListItem[]>({
    queryKey: queryKeys.reservations.vehiclesList(),
    queryFn: getVehiclesList,
    staleTime: 5 * 60 * 1000,
  });
}

interface UseFleetByCityOptions {
  enabled?: boolean;
}

export function useFleetByCity(
  cityId: number | null | undefined,
  options?: UseFleetByCityOptions
) {
  return useQuery<Fleet | null>({
    queryKey: queryKeys.reservations.fleetByCity(cityId ?? 0),
    queryFn: () => (cityId ? getFleetByCity(cityId) : Promise.resolve(null)),
    enabled: options?.enabled !== false && Boolean(cityId),
    placeholderData: null,
  });
}

interface UseReservationConfigByCityOptions {
  enabled?: boolean;
}

export function useReservationConfigByCityId(
  cityId: number | null | undefined,
  options?: UseReservationConfigByCityOptions
) {
  return useQuery<ReservationConfigWithDeposit | null>({
    queryKey: queryKeys.reservations.configByCity(cityId ?? 0),
    queryFn: () =>
      cityId ? getReservationConfigByCityId(cityId) : Promise.resolve(null),
    enabled: options?.enabled !== false && Boolean(cityId),
    placeholderData: null,
  });
}
